<template>
    <div>
        <div class="box view-nav-bar">
            <div class="d-flex align-items-center w-100">
                <div class="flex-grow-1">
                    <strong>{{ title }}</strong>
                    <span v-if="current && current.movix_id" class="text-muted ml-2">
                        #{{ current.movix_id }}
                    </span>
                    <a v-if="current && current.imdb_id"
                       :href="`https://www.imdb.com/title/${current.imdb_id}/`"
                       target="_blank"
                       rel="noopener"
                       class="ml-3">
                        IMDb: {{ current.imdb_id }}
                    </a>
                </div>
                <div class="d-flex align-items-center">
                    <router-link :to="'/history/' + movixId + '/month'"
                                 class="btn btn-outline-secondary mr-2">
                        Per Month
                    </router-link>
                    <b-button variant="primary" :disabled="running" @click="onUpdate">
                        <spinner-bars v-if="running"/>
                        <span v-else>Update</span>
                    </b-button>
                </div>
            </div>
        </div>

        <div v-if="!loaded" class="box">
            <div class="box-content text-center text-muted py-4">Loading…</div>
        </div>

        <div v-else-if="!current" class="box">
            <div class="box-content text-center text-muted py-4">
                Movie not found.
                <router-link to="/history/search" class="ml-2">Back to search</router-link>
            </div>
        </div>

        <div v-else>
            <div class="row">
                <div class="col-6">
                    <div class="box">
                        <div class="box-content">
                            <heading upper="Metadata" border/>
                            <dl class="row mb-0">
                                <dt class="col-5">Year</dt><dd class="col-7">{{ current.year || '—' }}</dd>
                                <dt class="col-5">Released</dt><dd class="col-7">{{ formatDate(current.released_at) }}</dd>
                                <dt class="col-5">Genres</dt><dd class="col-7">{{ current.genre_list || '—' }}</dd>
                                <dt class="col-5">Countries</dt><dd class="col-7">{{ current.countries || '—' }}</dd>
                                <dt class="col-5">Movie tag</dt><dd class="col-7">{{ current.movie_tag || '—' }}</dd>
                                <dt class="col-5">First admissions</dt><dd class="col-7">{{ formatDate(current.first_admissions_at) }}</dd>
                                <dt class="col-5">Last admissions</dt><dd class="col-7">{{ formatDate(current.last_admissions_at) }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>

                <div class="col-6">
                    <div class="box">
                        <div class="box-content">
                            <heading upper="Admissions" border/>
                            <dl class="row mb-0">
                                <dt class="col-5">Belgium total</dt><dd class="col-7">{{ formatInt(current.total_admissions_belgium) }}</dd>
                                <dt class="col-5">Brightfish total</dt><dd class="col-7">{{ formatInt(current.total_admissions_brightfish) }}</dd>
                                <dt class="col-5">Papayas</dt><dd class="col-7">{{ formatInt(current.papayas) }}</dd>
                                <dt class="col-5">Burn rate</dt><dd class="col-7">{{ current.burn_rate_weeks !== null ? current.burn_rate_weeks + ' weeks' : '—' }}</dd>
                            </dl>
                            <heading upper="Yearly Brightfish totals" border class="mt-3">
                                <template v-slot:lower>
                                    <ul class="list-unstyled mb-0">
                                        <li v-for="(yt, idx) in yearlyTotals" :key="idx">
                                            {{ yt.year || '—' }}: {{ formatInt(yt.total_brightfish_admissions) }}
                                        </li>
                                    </ul>
                                </template>
                            </heading>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-6">
                    <div class="box">
                        <div class="box-content">
                            <heading upper="Geography" border/>
                            <dl class="row mb-0">
                                <dt class="col-5">North</dt><dd class="col-7">{{ pct(current.fraction_geo_north) }}</dd>
                                <dt class="col-5">South</dt><dd class="col-7">{{ pct(current.fraction_geo_south) }}</dd>
                                <dt class="col-5">Brussels</dt><dd class="col-7">{{ pct(current.fraction_geo_brussels) }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
                <div class="col-6">
                    <div class="box">
                        <div class="box-content">
                            <heading upper="Languages" border/>
                            <dl class="row mb-0">
                                <dt class="col-5">NL</dt><dd class="col-7">{{ pct(current.fraction_lang_nl) }}</dd>
                                <dt class="col-5">FR</dt><dd class="col-7">{{ pct(current.fraction_lang_fr) }}</dd>
                                <dt class="col-5">VO</dt><dd class="col-7">{{ pct(current.fraction_lang_vo) }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>

            <div class="box">
                <div class="box-content">
                    <heading upper="Per-site distribution (top 20)" border/>
                    <div v-if="!siteRows.length" class="text-muted pt-2">No per-site data.</div>
                    <table v-else class="shx-table w-100 mt-2">
                        <thead>
                            <tr>
                                <th class="shx-th-120">Theatre</th>
                                <th class="shx-th-100 text-right">Share</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in siteRows" :key="row.code">
                                <td>{{ row.code }}</td>
                                <td class="text-right">{{ pct(row.fraction) }}</td>
                                <td>
                                    <div class="bg-secondary"
                                         :style="{width: Math.round(row.fraction * 100) + '%', height: '8px'}"></div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="box">
                <div class="box-content text-muted">
                    <small>
                        Created: {{ formatDateTime(current.created_at) }}
                        &nbsp;·&nbsp;
                        Last compiled: {{ formatDateTime(current.updated_at) }}
                    </small>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { BButton } from "bootstrap-vue";
import Heading from "../partials/Heading";
import SpinnerBars from "../partials/SpinnerBars";

export default {
    components: {
        BButton,
        Heading,
        SpinnerBars,
    },
    props: ['movixId'],
    data() {
        return {
            running: false,
            loaded: false,
        };
    },
    computed: {
        current() {
            return this.$store.getters.historyCurrent;
        },
        title() {
            return this.current && this.current.movix_title
                ? this.current.movix_title
                : `Movix #${this.movixId}`;
        },
        yearlyTotals() {
            return (this.current && this.current.yearly_totals) || [];
        },
        siteRows() {
            const sites = (this.current && this.current.fraction_sites) || null;
            if (!sites || typeof sites !== 'object') {
                return [];
            }
            return Object.entries(sites)
                .map(([code, fraction]) => ({ code, fraction: Number(fraction) || 0 }))
                .sort((a, b) => b.fraction - a.fraction)
                .slice(0, 20);
        },
    },
    watch: {
        movixId() {
            this.load();
        },
    },
    created() {
        this.load();
    },
    methods: {
        load() {
            this.loaded = false;
            this.$store
                .dispatch('fetchHistory', this.movixId)
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.loaded = true;
                });
        },
        onUpdate() {
            this.running = true;
            this.$store
                .dispatch('compileHistory', this.movixId)
                .then(() => this.$toast.success('History recompiled.'))
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.running = false;
                });
        },
        pct(v) {
            return v !== null && v !== undefined ? Math.round(v * 100) + '%' : '—';
        },
        formatInt(v) {
            return (v || 0).toLocaleString();
        },
        formatDate(v) {
            return v ? this.$dayjs(v).format('YYYY-MM-DD') : '—';
        },
        formatDateTime(v) {
            return v ? this.$dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—';
        },
    },
};
</script>
